<template>
  <main class="main">
    <portal to="headerTitle">
      {{ $t('admin.pagePrivacyPolicy.title') }}
    </portal>
    <template>
      <tabable-card cols="12 6@desktop" rows="1" @activeCardIndex="onLanguageChange($event)">
        <template v-for="language in hotelLanguages">
          <tabable-card-content :key="language" :title="$t(`lang.${language}`)" :full-size="true">
            <hm-card-inner v-if="!isNil(data) && !isNil(data.policy)" :full-size="true">
              <hm-col cols="12">
                <ckeditor v-model="data.policy[language]"
                          :ref="`editor_${language}`"
                          :id="`editor_${language}`"
                          :incard="true"
                          height="500" />
              </hm-col>
            </hm-card-inner>
          </tabable-card-content>
        </template>
      </tabable-card>
      <tabable-card cols="12 6@desktop" rows="3">
        <tabable-card-content :title="$t('admin.pagePrivacyPolicy.card.preview')">
          <hm-card-inner v-if="!hotelPrivacyPolicyUsed" :full-size="true">
            <span class="info-text info-text--full-size info-text--no-margin col-12">{{ $t('admin.pagePrivacyPolicy.defaultPolicyDisclaimer') }}</span>
          </hm-card-inner>
          <hm-card-inner :full-size="true">
            <span class="info-text info-text--full-size info-text--no-margin col-12">{{ $t('admin.pagePrivacyPolicy.infoPlaceholders') }}</span>
          </hm-card-inner>
          <hm-card-inner>
            <hm-col cols="12">
              <iframe :srcdoc="`<div style='font-family: sans-serif;font-size:13px;'>${getPreview(activeLanguage)}</div>`"
                      width="100%"
                      style="flex-grow: 1;overflow: auto;border:none;height:calc(100vh - 35rem);"/>
            </hm-col>
          </hm-card-inner>
        </tabable-card-content>
        <tabable-card-content :title="$t('admin.pagePrivacyPolicy.card.systemDefault')">
          <hm-card-inner v-if="hotelPrivacyPolicyUsed" :full-size="true">
            <span class="info-text info-text--full-size info-text--no-margin col-12">{{ $t('admin.pagePrivacyPolicy.hotelPrivacyPolicyUsed') }}</span>
          </hm-card-inner>
          <hm-card-inner v-else :full-size="true">
            <span class="warning-text warning-text--full-size warning-text--no-margin col-12">{{ $t('admin.pagePrivacyPolicy.systemPrivacyPolicyUsed') }}</span>
          </hm-card-inner>
          <hm-card-inner :full-size="true">
            <span class="info-text info-text--full-size info-text--no-margin col-12">{{ $t('admin.pagePrivacyPolicy.defaultPolicyDisclaimer') }}</span>
          </hm-card-inner>
          <hm-card-inner>
            <hm-col cols="12">
              <div v-html="getSystemDefault(activeLanguage)" />
            </hm-col>
          </hm-card-inner>
        </tabable-card-content>
      </tabable-card>
    </template>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { cloneDeep, first, groupBy, isEmpty, isEqual, isNil, keyBy, map, maxBy, pullAt, sortBy } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import HmCardInner from '../../lib/layout/HmCardInner';
import HmMultiselect from '../../lib/inputs/HmMultiselect';
import HmButton from '../../lib/buttons/HmButton';
import uuid from 'uuid';
import TabableCard from '../../lib/layout/TabableCard';
import TabableCardContent from '../../lib/layout/TabableCardContent';
import Card from '../../lib/layout/Card.vue';

export default {
  name: 'PagePrivacyPolicy',
  components: { HmCardInner, Card, TabableCardContent, TabableCard },
  mixins: [HotelmeisterMixin, SaveWatchMixin],

  autosave: [
    {
      name: 'privacyPolicy',
      variable: 'data',
      fn: function (cbSaveComplete) {
        const toSave = cloneDeep(this.data);
        toSave.defaultPolicy = null;

        this.axios.put(this.serviceUrls.privacyPolicy.get(this, this.type, this.countryCode), toSave)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();
          })
          .catch((error) => {
            if (isNil(error.response)) this.showErrorMessage();
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  data() {
    return {
      countryCode: this.$route.params.countryCode,
      type: this.$route.params.type,
      dataLoaded: false,
      data: null,
      activeLanguage: null,
    };
  },

  computed: {
    isSystemRootInstance: function () {
      return ['system', 'hotelmeister'].includes(this.hotelSettings.tenant.subdomain.toLowerCase());
    },
    hotelLanguages: function () {
      return [this.hotelSettings.details.primaryLanguage, ...this.hotelSettings.details.additionalLanguages];
    },
    hotelFallbackLanguages: function () {
      return [this.hotelSettings.details.fallbackLanguage, this.hotelSettings.details.primaryLanguage];
    },
    hotelPrivacyPolicyUsed: function () {
      const policy = ((this.data || {}).policy || {});
      let hotelPolicy = null;
      if (!isEmpty(policy[this.activeLanguage])) hotelPolicy = policy[this.activeLanguage];
      else hotelPolicy = first(this.hotelFallbackLanguages.map(it => policy[it]).filter(it => !isEmpty(it)));

      return !isEmpty(hotelPolicy);
    },
  },

  watch: {
    '$route.params': {
      handler: function (params) {
        this.as__autosave();
        this.countryCode = params.countryCode;
        this.type = params.type;
        this.loadData();
      },
      deep: true,
    },
  },

  created: function () {
    this.loadData();
    this.activeLanguage = this.hotelLanguages[0];
  },

  methods: {
    isNil,
    loadData: function () {
      this.axios.get(this.serviceUrls.privacyPolicy.get(this, this.type, this.countryCode))
        .then((response) => {
          if (this.dataLoaded === false) {
            this.data = response.data.data;
            if (isNil(this.data.policy)) this.$set(this.data, 'policy', {});
            this.hotelLanguages.forEach((lang) => {
              if (isNil(this.data.policy[lang])) this.$set(this.data.policy, lang, null);
            });
            this.dataLoaded = true;
          }
        });
    },
    getPreview: function (lang) {
      if (this.hotelPrivacyPolicyUsed) {
        const policy = ((this.data || {}).policy || {});
        if (!isEmpty(policy[lang])) return policy[lang];
        return first(this.hotelFallbackLanguages.map(it => policy[it]).filter(it => !isEmpty(it)));
      } else {
        return this.getSystemDefault(lang);
      }
    },
    getSystemDefault: function (lang) {
      const policy = ((this.data || {}).defaultPolicy || {});
      if (!isEmpty(policy[lang])) return policy[lang];
      return first(this.hotelFallbackLanguages.map(it => policy[it]).filter(it => !isEmpty(it)));
    },
    onLanguageChange: function (idx) {
      this.activeLanguage = this.hotelLanguages[idx];
    },
  },

};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PagePrivacyPolicy.vue